import styled, { keyframes } from "styled-components";

const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const slideUp = keyframes`from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; }`;

export const PageContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(8px);
  z-index: 2000;
  animation: ${fadeIn} 0.3s ease;
  padding: 20px;
  box-sizing: border-box;
`;

export const ContentBox = styled.div`
  background: #fff;
  border-radius: 20px;
  width: 100%;
  max-width: 900px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3);
  animation: ${slideUp} 0.4s ease-out;
  overflow: hidden;
`;

export const Header = styled.div`
  padding: 20px 30px;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #fff;
  h2 {
    margin: 0;
    font-size: 1.4rem;
    color: #111;
    span {
      color: #d4af37;
    }
  }
`;

export const FormBody = styled.div`
  padding: 30px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

export const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 18px;
`;

export const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  label {
    font-size: 0.78rem;
    font-weight: 700;
    color: #666;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  input,
  select {
    height: 50px;
    border: 1px solid #ddd;
    background: #fff;
    padding: 0 15px;
    color: #111;
    border-radius: 12px;
    font-size: 1rem;
    transition: all 0.2s ease;
    box-sizing: border-box;
    &:focus {
      outline: none;
      border-color: #d4af37;
      box-shadow: 0 0 0 4px rgba(212, 175, 55, 0.12);
    }
    &[readOnly] {
      background: #f7f7f7;
      color: #888;
      cursor: default;
    }
  }
`;

export const TextArea = styled.textarea`
  border: 1px solid #ddd;
  background: #fff;
  padding: 12px 15px;
  color: #111;
  border-radius: 12px;
  font-size: 0.95rem;
  resize: vertical;
  min-height: 90px;
  font-family: inherit;
  transition: all 0.2s ease;
  box-sizing: border-box;
  &:focus {
    outline: none;
    border-color: #d4af37;
    box-shadow: 0 0 0 4px rgba(212, 175, 55, 0.12);
  }
  &::placeholder { color: #bbb; }
`;

export const SelectClientBox = styled.div`
  height: 50px;
  border: 2px dashed #ddd;
  background: #fafafa;
  color: #888;
  border-radius: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 15px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.95rem;

  &:hover {
    border-color: #d4af37;
    color: #d4af37;
    background: #fffdf5;
  }
`;

export const SectionLabel = styled.p`
  font-size: 0.78rem;
  font-weight: 700;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin: 0 0 10px 0;
`;

export const RadioGroup = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

export const RadioCard = styled.label`
  flex: 1;
  min-width: 100px;
  padding: 14px 18px;
  border-radius: 12px;
  border: 2px solid ${(props) => (props.checked ? "#d4af37" : "#eee")};
  background: ${(props) => (props.checked ? "#fffdf5" : "#fff")};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.95rem;
  font-weight: 600;
  color: ${(props) => (props.checked ? "#111" : "#555")};

  input[type="radio"] {
    accent-color: #d4af37;
    width: 16px;
    height: 16px;
    cursor: pointer;
  }

  &:hover {
    border-color: #d4af37;
  }
`;

export const Footer = styled.div`
  padding: 20px 30px;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: flex-end;
  background: #fdfdfd;
`;

export const ConfirmButton = styled.button`
  background: #d4af37;
  color: #000;
  border: none;
  border-radius: 14px;
  padding: 0 40px;
  height: 54px;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover:not(:disabled) {
    background: #c5a02d;
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(212, 175, 55, 0.25);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  z-index: 2500;
  display: flex;
  justify-content: center;
  align-items: center;
  animation: ${fadeIn} 0.3s;
`;

export const ModalContent = styled.div`
  background: white;
  border-radius: 18px;
  width: 90%;
  max-width: 600px;
  padding: 25px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.2);
  animation: ${slideUp} 0.35s ease-out;
`;

export const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 18px;
  h3 {
    margin: 0;
    font-size: 1.1rem;
    color: #111;
  }
`;

export const CloseButton = styled.button`
  background: transparent;
  border: none;
  font-size: 1.3rem;
  color: #aaa;
  cursor: pointer;
  transition: color 0.2s;
  &:hover {
    color: #ff4d4d;
  }
`;

export const SearchWrapper = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 14px;
`;

export const SearchInput = styled.input`
  flex-grow: 1;
  height: 50px;
  border: 1px solid #ddd;
  border-radius: 12px;
  padding: 0 15px;
  font-size: 1rem;
  box-sizing: border-box;
  transition: all 0.2s;
  &:focus {
    outline: none;
    border-color: #d4af37;
    box-shadow: 0 0 0 4px rgba(212, 175, 55, 0.12);
  }
`;

export const SearchButton = styled.button`
  height: 50px;
  width: 55px;
  border: none;
  background: #111;
  color: white;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  cursor: pointer;
  transition: background 0.2s;
  &:hover {
    background: #333;
  }
`;

export const ClientList = styled.div`
  max-height: 50vh;
  overflow-y: auto;
  border: 1px solid #eee;
  border-radius: 12px;
  background: #fafafa;
`;

export const ClientItem = styled.div`
  padding: 14px 18px;
  border-bottom: 1px solid #eee;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: background 0.2s;
  p {
    margin: 0;
    font-weight: 600;
    color: #111;
  }
  span {
    font-size: 0.85rem;
    color: #777;
    font-family: monospace;
  }
  &:hover {
    background: #fffdf5;
  }
  &:last-child {
    border-bottom: none;
  }
`;
